import { memo, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'

import LargeContentToggle from 'components/Common/LargeContentToggle'
import { useElementHeight } from 'hooks/useElementSize'
import useDidChange from 'hooks/useDidChange'
import useToggle from 'hooks/useToggle'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import type { ExperiencesPassportOfferSource } from 'components/Experiences/ExperiencesPassport/experiencesPassportTypes'

import type { PropertyRateInclusionListsProps } from './PropertyRateInclusionsStack'
import PropertyExpandedRateInclusions from './PropertyExpandedRateInclusions'

interface Props extends PropertyRateInclusionListsProps {
  passportOffer?: ExperiencesPassportOfferSource
  maxHeight: number
  /**
   * Loyalty upgrades: when the rate has a free package-upgrade target, both rates' inclusions render
   * stacked and `upgradeApplied` cross-fades between them as the upgrade is toggled.
   */
  upgradeTargetRate?: App.EnrichedPropertyRate
  upgradeApplied?: boolean
}

function PropertyRateInclusions({
  property,
  inclusions,
  bonusInclusions,
  luxPlusInclusions,
  bundleInclusions,
  passportOffer,
  maxHeight,
  upgradeTargetRate,
  upgradeApplied,
}: Props) {
  const intl = useIntl()
  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const { flights, rooms, charterBundleRoute } = useContext(OfferPageStateContext)
  const showReturnFlightsInclusion = flights && !charterBundleRoute && !property.mustBundleWithFlights
  const contentRef = useRef<HTMLDivElement>(null)
  const inclusionsSwapContainerRef = useRef<HTMLDivElement>(null)
  const baseRateInclusionsRef = useRef<HTMLDivElement>(null)
  const targetUpgradeRateInclusionsRef = useRef<HTMLDivElement>(null)
  const contentHeight = useElementHeight(contentRef)
  const inclusionsSwapContainerHeight = useElementHeight(inclusionsSwapContainerRef)
  const baseRateInclusionsHeight = useElementHeight(baseRateInclusionsRef)
  const targetUpgradeRateInclusionsHeight = useElementHeight(targetUpgradeRateInclusionsRef)
  const upgradeAppliedChanged = useDidChange(upgradeApplied, upgradeApplied)
  const {
    value: inclusionsExpanded,
    toggle: toggleInclusionsExpanded,
    off: collapseInclusions,
  } = useToggle()
  const inclusionsOpen = inclusionsExpanded && !upgradeAppliedChanged

  useEffect(() => {
    if (upgradeAppliedChanged) {
      collapseInclusions()
    }
  }, [collapseInclusions, upgradeAppliedChanged])

  const activeInclusionsHeight = upgradeTargetRate
    ? (upgradeApplied ? targetUpgradeRateInclusionsHeight : baseRateInclusionsHeight) ?? undefined
    : undefined
  const activeContentHeight = activeInclusionsHeight !== undefined &&
    contentHeight !== null && inclusionsSwapContainerHeight !== null
    ? contentHeight - inclusionsSwapContainerHeight + activeInclusionsHeight
    : undefined

  return <LargeContentToggle
    maxHeight={maxHeight}
    contentHeightOverride={activeContentHeight}
    open={inclusionsOpen}
    onToggle={toggleInclusionsExpanded}
    unconstrainedWhenExpanded={!!passportOffer}
    showMoreText={intl.formatMessage({
      id: '74uMot_LhpmF_fW845tr',
      defaultMessage: 'View more inclusions',
      description: 'Toggle text to expand inclusions list for property rate option',
    })}
    showLessText={intl.formatMessage({
      id: 'i_MD93nsLRPDKVjXbxqE',
      defaultMessage: 'View less inclusions',
      description: 'Toggle text to collapse inclusions list for property rate option',
    })}
  >
    <PropertyExpandedRateInclusions
      property={property}
      inclusions={inclusions}
      bonusInclusions={bonusInclusions}
      luxPlusInclusions={luxPlusInclusions}
      bundleInclusions={bundleInclusions}
      passportOffer={passportOffer}
      upgradeTargetRate={upgradeTargetRate}
      upgradeApplied={upgradeApplied}
      showReturnFlightsInclusion={showReturnFlightsInclusion}
      rooms={rooms}
      displayTier={displayTier}
      contentRef={contentRef}
      inclusionsSwapContainerRef={inclusionsSwapContainerRef}
      baseRateInclusionsRef={baseRateInclusionsRef}
      targetUpgradeRateInclusionsRef={targetUpgradeRateInclusionsRef}
    />
  </LargeContentToggle>
}

export default memo(PropertyRateInclusions)
